import { forwardRef } from "react";
import type { AriaButtonProps } from "react-aria";
import { Button as AriaButton, type SlotProps } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { Size } from "../helpers/helpers.js";

export const ButtonVariant = {
  Primary: "PRIMARY",
  Secondary: "SECONDARY",
  Tertiary: "TERTIARY",
  Warning: "WARNING",
  CallStart: "CALL_START",
  Destructive: "DESTRUCTIVE",
} as const;
export type ButtonVariant = (typeof ButtonVariant)[keyof typeof ButtonVariant];

export interface ButtonProps extends SlotProps {
  variant: ButtonVariant;
  size?: Size;
  icon?: React.ReactNode;
  onClick?: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
  type?: AriaButtonProps["type"];
  className?: string;
  shouldWrapText?: boolean;
}

export const buttonStyle = tv({
  base: "flex max-w-full flex-row items-center truncate disabled:shadow-none data-[pending]:cursor-progress",
  variants: {
    variant: {
      [ButtonVariant.Primary]:
        "rounded-md bg-primary font-semibold text-primary-foreground shadow-xs hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-disabled disabled:text-muted-foreground",
      [ButtonVariant.Secondary]:
        "rounded-md bg-white font-semibold text-foreground shadow-xs ring-1 ring-border-strong ring-inset hover:bg-gray-50 disabled:bg-disabled disabled:text-muted-foreground",
      [ButtonVariant.Tertiary]:
        "leading-6 font-semibold text-gray-700 hover:text-foreground disabled:text-muted-foreground",
      [ButtonVariant.Warning]:
        "rounded-md bg-white font-semibold text-red-500 shadow-xs ring-1 ring-border-strong ring-inset hover:bg-gray-50 disabled:bg-disabled disabled:text-muted-foreground",
      // TODO: Figure out how to make this meet WCAG AA contrast ratio.
      [ButtonVariant.CallStart]:
        "rounded-md bg-success font-semibold text-white shadow-xs hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:bg-disabled disabled:text-muted-foreground",
      [ButtonVariant.Destructive]:
        "rounded-md bg-destructive font-semibold text-white shadow-xs hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:bg-disabled disabled:text-muted-foreground",
    },
    size: {
      [Size.SMALL]: "h-7 space-x-2 px-2 text-xs",
      [Size.MEDIUM]: "h-8 space-x-2 px-3 text-sm",
      [Size.LARGE]: "h-10 space-x-3 px-4 text-base",
    },
  },
  compoundVariants: [
    {
      variant: ButtonVariant.Tertiary,
      size: Size.MEDIUM,
      class: "p-0",
    },
  ],
  defaultVariants: {
    variant: ButtonVariant.Secondary,
    size: Size.MEDIUM,
  },
});

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      onClick,
      isDisabled,
      size = Size.MEDIUM,
      variant,
      icon,
      className,
      shouldWrapText = false,
    } = props;

    // Generate button classes using tailwind-variants
    const buttonClassName = buttonStyle({
      variant,
      size,
      className,
    });

    const buttonContent = (
      <>
        {props.isLoading ? (
          <svg
            aria-hidden="true"
            className="inline h-4 w-4 shrink-0 animate-spin text-white"
            fill="none"
            role="status"
            viewBox="0 0 100 101"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="#E5E7EB"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentColor"
            />
          </svg>
        ) : null}

        {icon ? <div className="shrink-0">{icon}</div> : null}

        {props.children ? (
          <div
            className={twMerge(
              "grow",
              shouldWrapText ? "whitespace-normal" : "truncate",
            )}
          >
            {props.children}
          </div>
        ) : null}
      </>
    );

    return (
      <AriaButton
        ref={ref}
        type={props.type}
        slot={props.slot}
        className={buttonClassName}
        isDisabled={isDisabled}
        isPending={props.isLoading}
        // Drop the press event argument, since it's non-standard and we don't need it.
        onPress={onClick ? () => void onClick() : undefined}
      >
        {buttonContent}
      </AriaButton>
    );
  },
);
